import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Grid,
  Tabs,
  Tab,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  ShoppingCart as ProcureIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { useProcurementPlanner, useProcureRawMaterial } from '../hooks/useFeed';
import { format, addDays } from 'date-fns';
import {
  ProcurementPlannerBatchBreakdown,
  ProcurementPlannerBatchNoTemplate,
  ProcurementPlannerRawMaterial,
  ProcurementPlannerWeek,
  ProcurementPlannerSummary,
} from '../types';

const fmt = (n: number, decimals = 1) =>
  n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const StockChip: React.FC<{ deficitKg: number; neededKg: number }> = ({ deficitKg, neededKg }) => {
  if (deficitKg <= 0) {
    return <Chip label="Sufficient" color="success" size="small" icon={<CheckIcon />} />;
  }
  const pct = neededKg > 0 ? Math.round((deficitKg / neededKg) * 100) : 100;
  const color = pct > 50 ? 'error' : 'warning';
  return <Chip label={`Deficit ${pct}%`} color={color} size="small" icon={<WarningIcon />} />;
};

interface ProcureDialogProps {
  open: boolean;
  material: ProcurementPlannerRawMaterial | null;
  week1EndDate: string;
  onClose: () => void;
}

const ProcureDialog: React.FC<ProcureDialogProps> = ({ open, material, week1EndDate, onClose }) => {
  const procureMutation = useProcureRawMaterial();

  const defaultBags = material
    ? material.deficit_kg && material.default_kg_per_bag > 0
      ? Math.ceil(material.deficit_kg / material.default_kg_per_bag)
      : 1
    : 1;

  const defaultCostPerBag = material
    ? material.default_kg_per_bag > 0
      ? material.last_cost_per_unit / material.default_kg_per_bag
      : 0
    : 0;

  const [form, setForm] = useState({
    bags_purchased: defaultBags.toString(),
    cost_per_bag: defaultCostPerBag.toFixed(2),
    kg_per_bag: material?.default_kg_per_bag.toString() ?? '50',
    procurement_date: format(new Date(), 'yyyy-MM-dd'),
    delivery_date: week1EndDate,
    notes: '',
  });

  React.useEffect(() => {
    if (material) {
      const bags =
        material.deficit_kg && material.default_kg_per_bag > 0
          ? Math.ceil(material.deficit_kg / material.default_kg_per_bag)
          : 1;
      const cpb =
        material.default_kg_per_bag > 0
          ? material.last_cost_per_unit / material.default_kg_per_bag
          : 0;
      setForm({
        bags_purchased: bags.toString(),
        cost_per_bag: cpb.toFixed(2),
        kg_per_bag: material.default_kg_per_bag.toString(),
        procurement_date: format(new Date(), 'yyyy-MM-dd'),
        delivery_date: week1EndDate,
        notes: '',
      });
    }
  }, [material, week1EndDate]);

  const handleSubmit = async () => {
    if (!material) return;
    try {
      await procureMutation.mutateAsync({
        raw_material_id: material.id,
        bags_purchased: parseFloat(form.bags_purchased),
        cost_per_bag: parseFloat(form.cost_per_bag),
        kg_per_bag: parseFloat(form.kg_per_bag),
        procurement_date: form.procurement_date,
        delivery_date: form.delivery_date || undefined,
        notes: form.notes || undefined,
      });
      onClose();
    } catch (error) {
      console.error('Failed to create procurement:', error);
    }
  };

  const totalKg = parseFloat(form.bags_purchased || '0') * parseFloat(form.kg_per_bag || '0');
  const totalCost = parseFloat(form.bags_purchased || '0') * parseFloat(form.cost_per_bag || '0');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Procure Raw Material
        {material && (
          <Typography variant="body2" color="text.secondary">
            {material.name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {material?.deficit_kg != null && material.deficit_kg > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Deficit: <strong>{fmt(material.deficit_kg)} kg</strong> — suggested{' '}
              <strong>
                {material.default_kg_per_bag > 0
                  ? Math.ceil(material.deficit_kg / material.default_kg_per_bag)
                  : '?'}{' '}
                bags
              </strong>
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Bags to Purchase"
                type="number"
                value={form.bags_purchased}
                onChange={(e) => setForm((f) => ({ ...f, bags_purchased: e.target.value }))}
                fullWidth
                size="small"
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="kg per Bag"
                type="number"
                value={form.kg_per_bag}
                onChange={(e) => setForm((f) => ({ ...f, kg_per_bag: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Cost per Bag"
                type="number"
                value={form.cost_per_bag}
                onChange={(e) => setForm((f) => ({ ...f, cost_per_bag: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Procurement Date"
                type="date"
                value={form.procurement_date}
                onChange={(e) => setForm((f) => ({ ...f, procurement_date: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Expected Delivery Date"
                type="date"
                value={form.delivery_date}
                onChange={(e) => setForm((f) => ({ ...f, delivery_date: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notes (optional)"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                fullWidth
                size="small"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1.5 }}>
            <Typography variant="body2">
              Total quantity: <strong>{fmt(totalKg)} kg</strong>
            </Typography>
            <Typography variant="body2">
              Total cost: <strong>GHS {fmt(totalCost, 2)}</strong>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={procureMutation.isPending || !form.bags_purchased}
        >
          {procureMutation.isPending ? <CircularProgress size={20} /> : 'Create Procurement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface WeekTableProps {
  weekData: ProcurementPlannerWeek;
  isFirstWeek: boolean;
  onProcure: (material: ProcurementPlannerRawMaterial) => void;
}

const WeekTable: React.FC<WeekTableProps> = ({ weekData, isFirstWeek, onProcure }) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box>
      {weekData.batches_without_template.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Batches without a production template (feed total only):
          </Typography>
          {weekData.batches_without_template.map((b) => (
            <Typography key={b.batch_id} variant="body2">
              {b.batch_name} — {b.bird_count.toLocaleString()} birds, age {b.age_weeks} wks →{' '}
              <strong>{fmt(b.weekly_feed_needed_kg)} kg/wk</strong>
            </Typography>
          ))}
        </Alert>
      )}
      {weekData.raw_materials.length === 0 ? (
        <Alert severity="warning">
          No raw material data available. Ensure feed production templates are assigned to active
          batches.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(255, 152, 0, 0.08)' }}>
                <TableCell />
                <TableCell>Raw Material</TableCell>
                <TableCell align="right">Needed (kg)</TableCell>
                {isFirstWeek && <TableCell align="right">In Stock (kg)</TableCell>}
                {isFirstWeek && <TableCell align="right">To Procure (bags)</TableCell>}
                {isFirstWeek && <TableCell align="center">Status</TableCell>}
                <TableCell align="right">Est. Bags</TableCell>
                <TableCell align="right">Est. Cost</TableCell>
                {isFirstWeek && <TableCell align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {weekData.raw_materials.map((rm) => {
                const expanded = expandedRows.has(rm.id);
                const hasDeficit = isFirstWeek && (rm.deficit_kg ?? 0) > 0;
                return (
                  <React.Fragment key={rm.id}>
                    <TableRow
                      sx={{
                        bgcolor: hasDeficit ? 'error.50' : undefined,
                        '&:hover': { bgcolor: hasDeficit ? 'error.100' : 'action.hover' },
                      }}
                    >
                      <TableCell sx={{ width: 40, p: 0.5 }}>
                        <IconButton size="small" onClick={() => toggleRow(rm.id)}>
                          {expanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {rm.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rm.unit_of_measure}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{fmt(rm.needed_kg)}</TableCell>
                      {isFirstWeek && (
                        <TableCell align="right">
                          {rm.current_stock_kg != null ? fmt(rm.current_stock_kg) : '—'}
                        </TableCell>
                      )}
                      {isFirstWeek && (
                        <TableCell align="right">
                          {rm.deficit_bags != null && rm.deficit_bags > 0 ? (
                            <Typography variant="body2" color="error" fontWeight={600}>
                              {rm.deficit_bags.toLocaleString()}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="success.main">
                              0
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {isFirstWeek && (
                        <TableCell align="center">
                          <StockChip deficitKg={rm.deficit_kg ?? 0} neededKg={rm.needed_kg} />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {rm.estimated_bags != null ? rm.estimated_bags.toLocaleString() : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {rm.estimated_cost != null ? `GHS ${fmt(rm.estimated_cost, 2)}` : '—'}
                      </TableCell>
                      {isFirstWeek && (
                        <TableCell align="center">
                          <Tooltip title="Create procurement order">
                            <Button
                              size="small"
                              variant="outlined"
                              color={hasDeficit ? 'error' : 'primary'}
                              startIcon={<ProcureIcon />}
                              onClick={() => onProcure(rm)}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Procure
                            </Button>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={isFirstWeek ? 9 : 5} sx={{ py: 0 }}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1, px: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Batch breakdown
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Batch</TableCell>
                                  <TableCell align="right">Birds</TableCell>
                                  <TableCell align="right">Age (wks)</TableCell>
                                  <TableCell align="right">Weekly Feed (kg)</TableCell>
                                  <TableCell align="right">This Material (kg)</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rm.batch_breakdown.map((b) => (
                                  <TableRow key={b.batch_id}>
                                    <TableCell>{b.batch_name}</TableCell>
                                    <TableCell align="right">
                                      {b.bird_count.toLocaleString()}
                                    </TableCell>
                                    <TableCell align="right">{b.age_weeks}</TableCell>
                                    <TableCell align="right">{fmt(b.weekly_feed_kg)}</TableCell>
                                    <TableCell align="right">{fmt(b.raw_material_kg)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const SummaryTable: React.FC<{
  summary: ProcurementPlannerSummary[];
  weeks: number;
}> = ({ summary, weeks }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: 'rgba(255, 152, 0, 0.08)' }}>
          <TableCell>Raw Material</TableCell>
          <TableCell align="right">{weeks}-Week Total (kg)</TableCell>
          <TableCell align="right">Avg/Week (kg)</TableCell>
          <TableCell align="right">In Stock (kg)</TableCell>
          <TableCell align="right">Total Deficit (kg)</TableCell>
          <TableCell align="center">Weeks of Stock</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {summary.map((rm) => {
          const avgWeekly = weeks > 0 ? rm.total_kg / weeks : 0;
          const hasDeficit = rm.total_deficit_kg > 0;
          const weeksOfStock = rm.weeks_of_stock;
          return (
            <TableRow key={rm.id} sx={{ bgcolor: hasDeficit ? 'error.50' : undefined }}>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {rm.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {rm.unit_of_measure}
                </Typography>
              </TableCell>
              <TableCell align="right">{fmt(rm.total_kg)}</TableCell>
              <TableCell align="right">{fmt(avgWeekly)}</TableCell>
              <TableCell align="right">{fmt(rm.current_stock_kg)}</TableCell>
              <TableCell align="right">
                {hasDeficit ? (
                  <Typography variant="body2" color="error" fontWeight={600}>
                    {fmt(rm.total_deficit_kg)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="success.main">
                    0
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                {weeksOfStock != null ? (
                  <Chip
                    label={`${weeksOfStock} wks`}
                    color={weeksOfStock < 1 ? 'error' : weeksOfStock < 2 ? 'warning' : 'success'}
                    size="small"
                  />
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const ProcurementPlannerPage: React.FC = () => {
  const [weeks, setWeeks] = useState(4);
  const [activeTab, setActiveTab] = useState(0);
  const [procureDialogOpen, setProcureDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<ProcurementPlannerRawMaterial | null>(
    null
  );

  const { data, isLoading, isFetching, refetch } = useProcurementPlanner(weeks);

  const handleProcure = (material: ProcurementPlannerRawMaterial) => {
    setSelectedMaterial(material);
    setProcureDialogOpen(true);
  };

  const totalBatches = React.useMemo(() => {
    if (!data?.weeks_data[0]) return 0;
    const batches = new Set<number>();
    data.weeks_data[0].raw_materials.forEach((rm: ProcurementPlannerRawMaterial) =>
      rm.batch_breakdown.forEach((b: ProcurementPlannerBatchBreakdown) => batches.add(b.batch_id))
    );
    data.weeks_data[0].batches_without_template.forEach((b: ProcurementPlannerBatchNoTemplate) =>
      batches.add(b.batch_id)
    );
    return batches.size;
  }, [data]);

  const totalFeedKg =
    data?.weeks_data.reduce(
      (s: number, w: ProcurementPlannerWeek) => s + w.total_feed_needed_kg,
      0
    ) ?? 0;
  const totalCost =
    data?.weeks_data.reduce(
      (s: number, w: ProcurementPlannerWeek) => s + w.total_estimated_cost,
      0
    ) ?? 0;
  const week1EndDate =
    data?.weeks_data[0]?.end_date ?? format(addDays(new Date(), 6), 'yyyy-MM-dd');

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
          Feed Procurement Planner
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Weeks:
          </Typography>
          <IconButton
            size="small"
            onClick={() => setWeeks((w) => Math.max(1, w - 1))}
            disabled={weeks <= 1}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography variant="body1" fontWeight={600} sx={{ minWidth: 24, textAlign: 'center' }}>
            {weeks}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setWeeks((w) => Math.min(12, w + 1))}
            disabled={weeks >= 12}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <Button
          variant="outlined"
          startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Active Batches', value: totalBatches.toString(), color: 'primary.main' },
          {
            label: `Total Feed (${weeks} wks)`,
            value: `${fmt(totalFeedKg)} kg`,
            color: 'secondary.main',
          },
          {
            label: `Estimated Cost (${weeks} wks)`,
            value: `GHS ${fmt(totalCost, 2)}`,
            color: 'success.main',
          },
        ].map((card) => (
          <Grid size={{ xs: 12, sm: 4 }} key={card.label}>
            <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
              <Typography variant="body2" color="text.secondary">
                {card.label}
              </Typography>
              <Typography variant="h6" fontWeight={700} color={card.color}>
                {isLoading ? <CircularProgress size={20} /> : card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !data ? (
        <Alert severity="error">Failed to load procurement plan. Please refresh.</Alert>
      ) : (
        <>
          {/* Raw Material Summary */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            {weeks}-Week Raw Material Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generated {data.generated_at} · Total across all {weeks} weeks
          </Typography>
          {data.raw_material_summary.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No data yet. Make sure active batches have feed production templates assigned.
            </Alert>
          ) : (
            <Box sx={{ mb: 4 }}>
              <SummaryTable summary={data.raw_material_summary} weeks={weeks} />
            </Box>
          )}

          {/* Weekly Tabs */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Weekly Breakdown
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {data.weeks_data.map((w: ProcurementPlannerWeek, i: number) => (
                <Tab
                  key={w.week}
                  label={
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {w.label}
                      </Typography>
                      {i === 0 &&
                        w.raw_materials.some(
                          (rm: ProcurementPlannerRawMaterial) => (rm.deficit_kg ?? 0) > 0
                        ) && (
                          <Chip label="Action needed" color="error" size="small" sx={{ mt: 0.5 }} />
                        )}
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', textTransform: 'none', minWidth: 140 }}
                />
              ))}
            </Tabs>
          </Box>

          {data.weeks_data.map((weekData: ProcurementPlannerWeek, i: number) => (
            <Box key={weekData.week} hidden={activeTab !== i}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total feed needed: <strong>{fmt(weekData.total_feed_needed_kg)} kg</strong> · Est.
                  cost: <strong>GHS {fmt(weekData.total_estimated_cost, 2)}</strong>
                </Typography>
              </Box>
              <WeekTable weekData={weekData} isFirstWeek={i === 0} onProcure={handleProcure} />
            </Box>
          ))}
        </>
      )}

      <ProcureDialog
        open={procureDialogOpen}
        material={selectedMaterial}
        week1EndDate={week1EndDate}
        onClose={() => {
          setProcureDialogOpen(false);
          setSelectedMaterial(null);
        }}
      />
    </Box>
  );
};

export default ProcurementPlannerPage;
